import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import { smoothFactor } from '../utils/math';
import type { Projectile } from '../combat/Projectiles';

const scratchRelative = new Vector3();
const scratchClosest = new Vector3();
const scratchSafe = new Vector3();
const scratchBest = new Vector3();
const scratchAssistTarget = new Vector3();

/**
 * The Matrix dodge: when an attack is about to reach the player, time slows,
 * the camera is pointed at the attacker, and a near-miss assist nudges the body
 * out of the bolt path. The head tracker keeps running at full speed.
 *
 * Every detected attack opens a window; there is no cooldown between them.
 */
export class BulletTime {
  private running = false;
  private timer = 0;
  /** Total length of the current window, for the countdown bar. */
  private window = 0;
  /** Real seconds the current window has been running. */
  private elapsed = 0;
  private triggered = false;
  /**
   * Where the window's first detected attack came from.
   *
   * Several drones firing at once cannot all be watched, so the window focuses
   * on the first attack it saw: the camera turns to that attacker's muzzle and
   * the rest are left to the assist, which still avoids every bolt.
   */
  private readonly threatPosition = new Vector3(0, 0, -1);
  private readonly assist = new Vector3();
  /**
   * The assist follows one bolt at a time, not the sum of them all.
   *
   * A burst that straddles the body produces push-away directions on both
   * sides; summing them cancels to a near-zero vector that flips side frame to
   * frame, so the dodge jitters without committing. Instead the strongest
   * threat picks the direction, and it is held (with a small margin) until a
   * clearly stronger bolt on the other side takes over.
   */
  private readonly assistDir = new Vector3(0, 0, 0);
  private assistWeight = 0;
  private assistActive = false;
  /** False until the first smoothed frame, so the first push is immediate. */
  private assistPrimed = false;

  get active(): boolean {
    return this.running;
  }

  /**
   * The whole length of the current window, seconds.
   *
   * A window is stretched to cover the bolts still in the air, so this grows
   * past `bulletTime.duration`: the countdown bar needs it as its scale, or the
   * bar would pin at full and then collapse.
   */
  get span(): number {
    return this.window;
  }

  /** World and control scale: 1 normally, `bulletTime.scale` while dodging. */
  get timeScale(): number {
    return this.running ? BALANCE.bulletTime.scale : 1;
  }

  get remaining(): number {
    return this.timer;
  }

  /** True once per trigger, so the game can switch camera and warn the player. */
  consumeTrigger(): boolean {
    if (!this.triggered) return false;
    this.triggered = false;
    return true;
  }

  /** Where the attacker that opened this window fired from, for the camera. */
  getThreatPosition(out: Vector3): Vector3 {
    return out.copy(this.threatPosition);
  }

  /** Near-miss assist direction; zero length when there is nothing to avoid. */
  getAssist(out: Vector3): Vector3 {
    return out.copy(this.assist);
  }

  reset(): void {
    this.running = false;
    this.timer = 0;
    this.window = 0;
    this.elapsed = 0;
    this.triggered = false;
    this.clearAssist();
  }

  private clearAssist(): void {
    this.assist.set(0, 0, 0);
    this.assistDir.set(0, 0, 0);
    this.assistWeight = 0;
    this.assistActive = false;
    this.assistPrimed = false;
  }

  /**
   * Start a dodge window on demand, as an incoming burst would.
   *
   * Nothing in the game asks for one directly; it exists for scripts and
   * tests that need the slowed world without waiting for a drone to shoot.
   */
  startDodge(): void {
    this.beginWindow();
    this.clearAssist();
  }

  private beginWindow(): void {
    this.running = true;
    this.timer = BALANCE.bulletTime.duration;
    this.window = BALANCE.bulletTime.duration;
    this.elapsed = 0;
  }

  update(dt: number, bolts: readonly Projectile[], playerPosition: Vector3): void {
    const config = BALANCE.bulletTime;
    /** The first attack detected this frame, which is the one to focus on. */
    let detected = false;
    /** Real seconds the window has to stay open for the bolts still coming. */
    let hold = 0;
    /** The strongest threat this frame picks the assist direction, or -1. */
    let bestWeight = -1;
    scratchBest.set(0, 0, 0);

    for (const bolt of bolts) {
      scratchRelative.subVectors(playerPosition, bolt.position);
      const speedSq = bolt.velocity.lengthSq();
      if (speedSq < 1e-6) continue;
      // World seconds until the bolt is at its closest approach. Negative means
      // it is already past, so it is no longer on its way.
      const time = scratchRelative.dot(bolt.velocity) / speedSq;
      if (time < 0) continue;
      scratchClosest.copy(bolt.position).addScaledVector(bolt.velocity, time);
      const miss = scratchClosest.distanceTo(playerPosition);

      // Hold the dodge open while a bolt is still on its way and will pass
      // close enough to read as a near miss, so the window covers the whole
      // burst instead of a fixed count. A bolt that expires before it arrives
      // cannot reach the body, so it does not hold anything open. The bolt
      // closes the distance at `scale` world seconds per real second, which
      // makes `time / scale` a hold that ticks down in real time, one for one.
      if (time <= bolt.life && miss <= config.holdRadius) {
        hold = Math.max(hold, time / config.scale + config.holdMargin);
      }

      if (time > config.threatWindow) continue;
      if (miss > config.threatRadius) continue;

      if (!detected) {
        detected = true;
        // Focus on the attacker, not the bolt: where the shot was fired from is
        // what the camera should be pointed at, and it does not move.
        this.threatPosition.copy(bolt.origin);
      }

      // Push away from the bolt's closest approach. Only the strongest threat
      // is used: summing every bolt cancels when a burst straddles the body and
      // leaves the dodge flipping side to side.
      scratchSafe.subVectors(playerPosition, scratchClosest);
      if (scratchSafe.lengthSq() <= 1e-8) continue;
      const weight = 1 - miss / config.threatRadius;
      if (weight > bestWeight) {
        bestWeight = weight;
        scratchBest.copy(scratchSafe).normalize();
      }
    }

    this.updateAssist(bestWeight, dt);
    if (this.running) this.advance(dt, hold);

    // Every detected attack opens a window: no cooldown, and no minimum burst
    // size, so a single bolt is dodged just as a volley is.
    //
    // Checked after the window has been advanced, so a burst that is still
    // coming when one window ends opens the next one on the same frame. Doing it
    // the other way round left a single frame with no window in between, and
    // the game spent that frame dropping back to third person and hiding the
    // warning before the next window pulled it all forward again.
    if (!this.running && detected) {
      this.beginWindow();
      this.triggered = true;
      // Zero seconds on the opening frame: the window is already stretched to
      // cover the burst, so `span` and the countdown bar are right immediately.
      this.advance(0, hold);
    }
  }

  /**
   * Age the window by `dt`, stretch it to the bolts still on their way, and
   * close it once it is done.
   */
  private advance(dt: number, hold: number): void {
    const config = BALANCE.bulletTime;
    this.elapsed += dt;
    // Stretch the window to cover the bolts still in the air, but never past
    // the cap: one window must not outlive a long burst.
    if (hold > this.timer) this.timer = hold;
    const ceiling = Math.max(0, config.maxDuration - this.elapsed);
    if (this.timer > ceiling) this.timer = ceiling;
    if (this.timer > this.window) this.window = this.timer;
    this.timer -= dt;
    if (this.timer <= 0) {
      this.running = false;
      this.window = 0;
      this.clearAssist();
    }
  }

  /**
   * Pick and smooth the near-miss assist from this frame's strongest threat.
   *
   * `bestWeight` is `1 - miss/threatRadius` for the closest-to-deadly bolt, or
   * -1 when nothing is threatening. The chosen direction is held against a small
   * margin so two bolts of similar weight on opposite sides cannot make it flip,
   * and the published vector is exponentially smoothed so a switch eases rather
   * than snaps. The first push is applied whole, so the assist responds at once,
   * and when the threat passes the assist eases home.
   */
  private updateAssist(bestWeight: number, dt: number): void {
    const config = BALANCE.bulletTime;
    // The claim on a direction fades, so a stale push lets go when its bolt
    // leaves and a different threat can take over.
    this.assistWeight = Math.max(0, this.assistWeight - config.assistRelease * dt);
    if (this.assistActive && this.assistWeight <= 1e-4) this.assistActive = false;

    scratchAssistTarget.set(0, 0, 0);
    if (bestWeight >= 0) {
      const opposite = this.assistActive && scratchBest.dot(this.assistDir) < 0;
      if (
        !this.assistActive ||
        !opposite ||
        bestWeight > this.assistWeight + config.assistHysteresis
      ) {
        this.assistDir.copy(scratchBest);
        this.assistWeight = bestWeight;
        this.assistActive = true;
      } else {
        // Same side (or not clearly stronger): keep the direction.
        this.assistWeight = Math.max(this.assistWeight, bestWeight);
      }
      scratchAssistTarget.copy(this.assistDir).multiplyScalar(config.assistSpeed * bestWeight);
    }

    if (!this.assistPrimed) {
      this.assist.copy(scratchAssistTarget);
      this.assistPrimed = true;
    } else {
      this.assist.lerp(scratchAssistTarget, smoothFactor(config.assistSmoothing, dt));
      if (scratchAssistTarget.lengthSq() === 0 && this.assist.lengthSq() < 1e-6) {
        this.assist.set(0, 0, 0);
      }
    }
  }
}
